import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  SearchOff as SearchOffIcon,
  ArrowForward as ArrowForwardIcon,
  PersonAdd as PersonAddIcon,
  TipsAndUpdates as TipsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/**
 * NoResults Component
 *
 * Empty state for search with no results
 *
 * Features:
 * - Friendly "no results" message
 * - Search tips and suggestions
 * - "Browse all people" link
 * - "Add this person" button
 * - Helpful icons and formatting
 *
 * @component
 * @param {NoResultsProps} props - Component props
 * @returns {JSX.Element} No results state
 */
interface NoResultsProps {
  query: string;
}

export const NoResults: React.FC<NoResultsProps> = ({ query }) => {
  const navigate = useNavigate();

  // ===== Event Handlers =====
  const handleBrowseAll = () => {
    navigate('/people');
  };

  const handleAddPerson = () => {
    navigate('/people/add');
  };

  // ===== Render =====
  return (
    <Paper
      sx={{
        p: 6,
        textAlign: 'center',
        backgroundColor: 'background.default',
      }}
    >
      {/* Icon */}
      <Box sx={{ mb: 3 }}>
        <SearchOffIcon
          sx={{
            fontSize: 80,
            color: 'text.secondary',
            opacity: 0.5,
          }}
        />
      </Box>

      {/* Main Message */}
      <Typography variant="h5" gutterBottom>
        No results found for "{query}"
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        We couldn't find anyone matching your search.
      </Typography>

      {/* Search Tips */}
      <Box sx={{ mb: 4, textAlign: 'left', maxWidth: 600, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TipsIcon color="primary" />
          <Typography variant="h6">Search Tips</Typography>
        </Box>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <ArrowForwardIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Try using different keywords"
              secondary="Search by first name, last name, or maiden name"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <ArrowForwardIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Check your spelling"
              secondary="Make sure names are spelled correctly"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <ArrowForwardIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Use fewer filters"
              secondary="Remove some filters to broaden your search"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <ArrowForwardIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Try partial names"
              secondary="Search for just the first or last name"
            />
          </ListItem>
        </List>
      </Box>

      {/* Action Buttons */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="center"
      >
        <Button
          variant="contained"
          size="large"
          onClick={handleBrowseAll}
          startIcon={<SearchOffIcon />}
        >
          Browse All People
        </Button>
        <Button
          variant="outlined"
          size="large"
          onClick={handleAddPerson}
          startIcon={<PersonAddIcon />}
        >
          Add This Person
        </Button>
      </Stack>

      {/* Additional Help */}
      <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary">
          Can't find who you're looking for?{' '}
          <Typography
            component="span"
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={handleAddPerson}
          >
            Add them to the family tree
          </Typography>
        </Typography>
      </Box>
    </Paper>
  );
};
